import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  GraduationCap, 
  ClipboardCheck, 
  Users, 
  Target,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Heart
} from 'lucide-react';

const APropos = () => {
  return (
    <>
      <Helmet>
        <title>À propos de Mehdi Richa | Formateur & Auditeur Qualité Vaucluse</title>
        <meta 
          name="description" 
          content="Découvrez le parcours de Mehdi Richa, formateur polyvalent et auditeur qualité dans le Vaucluse. Expérience terrain, pédagogie concrète, proximité avec les acteurs locaux." 
        />
        <meta property="og:title" content="À propos - Mehdi Richa, Formateur & Auditeur Qualité" />
        <meta property="og:description" content="Mon parcours, ma philosophie et mon approche en tant que formateur et auditeur qualité dans le Vaucluse." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/a-propos`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="apropos-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                À propos
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                À propos de Mehdi
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Formateur polyvalent et auditeur qualité, j'accompagne depuis plusieurs années 
                les entreprises, les organismes de formation et les établissements scolaires 
                du Vaucluse dans leurs projets de formation et d'amélioration continue.
              </p>
              <div className="flex items-center gap-3 text-slate-600">
                <MapPin className="w-5 h-5 text-blue-600" />
                <span>Basé à Avignon, Vaucluse</span>
              </div>
            </div>
            <div className="animate-fade-in animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-600/10 rounded-3xl transform -rotate-3" />
                <div className="relative bg-white rounded-2xl shadow-xl p-8">
                  <div className="text-center mb-6">
                    <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mb-4">
                      <span className="text-white text-4xl font-serif font-bold">MR</span>
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-slate-900">Mehdi Richa</h2>
                    <p className="text-slate-600">Formateur & Auditeur Qualité</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 text-sm">Formateur polyvalent</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <ClipboardCheck className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 text-sm">Auditeur qualité</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 text-sm">Vaucluse (Avignon)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section className="py-20 md:py-28 bg-white" data-testid="apropos-parcours">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-8 animate-fade-in">
              Mon parcours
            </h2>
            <div className="prose prose-slate max-w-none animate-fade-in animation-delay-100">
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Mon parcours professionnel m'a permis d'acquérir une solide expérience terrain 
                dans les domaines de la <strong>logistique</strong>, de la <strong>qualité</strong> 
                et du <strong>management</strong>. Cette double casquette opérationnelle et pédagogique 
                est au cœur de mon approche.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Avant de me consacrer pleinement à la formation et au conseil, j'ai occupé des 
                fonctions opérationnelles qui m'ont donné une vision concrète des enjeux rencontrés 
                par les entreprises et les équipes au quotidien.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Aujourd'hui, je mets cette expérience au service des apprenants et des professionnels 
                du Vaucluse, en privilégiant une approche <strong>pragmatique</strong> et 
                <strong> orientée résultats</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophie */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="apropos-philosophie">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ma philosophie
            </h2>
            <p className="text-slate-600">
              Des convictions qui guident mon approche de la formation et de l'audit
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-100" data-testid="philo-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Pédagogie concrète
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Je privilégie les cas pratiques, les mises en situation et les exemples issus 
                  du terrain. L'apprentissage doit être ancré dans le réel pour être efficace.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-200" data-testid="philo-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <ClipboardCheck className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Audits pragmatiques
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Un audit doit être utile, pas bureaucratique. Mon objectif : vous aider à 
                  progresser avec des constats clairs et des recommandations applicables.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in animation-delay-300" data-testid="philo-3">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Heart className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-3">
                  Proximité locale
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Basé dans le Vaucluse, je connais les acteurs locaux et leurs spécificités. 
                  Cette proximité me permet d'être réactif et disponible pour mes clients.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Double posture */}
      <section className="py-20 md:py-28 bg-white" data-testid="apropos-posture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Une double posture complémentaire
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Ce qui fait ma spécificité, c'est cette double casquette de <strong>formateur</strong> 
                et d'<strong>auditeur qualité</strong>. Ces deux activités se nourrissent mutuellement :
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">L'audit enrichit ma pédagogie</h3>
                    <p className="text-slate-600 text-sm">
                      Les audits me permettent de rester connecté aux réalités opérationnelles 
                      des entreprises et d'illustrer mes formations avec des cas concrets actuels.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">La formation enrichit mes audits</h3>
                    <p className="text-slate-600 text-sm">
                      Mon expérience de formateur me permet d'adopter une posture pédagogique 
                      lors des audits : expliquer, transmettre, accompagner plutôt que simplement constater.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Un accompagnement global</h3>
                    <p className="text-slate-600 text-sm">
                      Pour les structures qui le souhaitent, je peux intervenir sur les deux volets : 
                      former vos équipes ET auditer vos pratiques.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="animate-fade-in animation-delay-200">
              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-slate-50 border-0 p-6">
                  <GraduationCap className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Formateur</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li>• Logistique & transport</li>
                    <li>• Qualité</li>
                    <li>• Management</li>
                    <li>• CEJM</li>
                  </ul>
                </Card>
                <Card className="bg-slate-50 border-0 p-6">
                  <ClipboardCheck className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Auditeur</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li>• Audits internes</li>
                    <li>• Audits blancs</li>
                    <li>• Diagnostics</li>
                    <li>• Revue documentaire</li>
                  </ul>
                </Card>
                <Card className="bg-blue-600 border-0 p-6 col-span-2">
                  <Users className="w-10 h-10 text-white mb-4" />
                  <h3 className="font-semibold text-white mb-2">Zone d'intervention</h3>
                  <p className="text-blue-100 text-sm">
                    Vaucluse : Avignon, Carpentras, Orange, Cavaillon, Apt, L'Isle-sur-la-Sorgue 
                    et leurs environs. Également disponible à distance.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="apropos-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6 animate-fade-in">
            Envie d'échanger ?
          </h2>
          <p className="text-slate-300 text-lg mb-10 animate-fade-in animation-delay-100">
            Que vous ayez un projet de formation ou d'audit, je serai ravi d'en discuter avec vous. 
            Premier échange gratuit et sans engagement.
          </p>
          <Button 
            asChild 
            size="lg"
            className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-lg animate-fade-in animation-delay-200"
            data-testid="apropos-cta-button"
          >
            <a href="mailto:mehdi.richa.formation@gmail.com">
              Me contacter
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </section>
    </>
  );
};

export default APropos;
